/**
 * useProjects Hook
 * Provides access to project data with derived computations
 */

import { useAtom } from "jotai";
import { useMemo, useCallback } from "react";
import { projectsQueryAtom } from "@/store/referenceDataStore";
import type { IServiceProject } from "@/services";

export interface UseProjectsReturn {
  /** All projects from API */
  projects: IServiceProject[];
  /** Active (non-archived) projects */
  activeProjects: IServiceProject[];
  /** Projects sorted alphabetically by name */
  sortedProjects: IServiceProject[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
  /** Find project by ID */
  findById: (id: number) => IServiceProject | undefined;
  /** Find project by code */
  findByCode: (code: string) => IServiceProject | undefined;
}

export function useProjects(): UseProjectsReturn {
  const [query] = useAtom(projectsQueryAtom);

  // Stable reference to prevent unnecessary recalculations
  const projects = useMemo(() => query.data ?? [], [query.data]);

  // Active (non-archived) projects
  const activeProjects = useMemo(
    () => projects.filter((p) => !p.is_archived),
    [projects],
  );

  // Sort alphabetically by name
  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => a.name.localeCompare(b.name)),
    [projects],
  );

  // Find by ID
  const findById = useCallback(
    (id: number) => projects.find((p) => p.id === id),
    [projects],
  );

  // Find by code
  const findByCode = useCallback(
    (code: string) => projects.find((p) => p.code === code),
    [projects],
  );

  return {
    projects,
    activeProjects,
    sortedProjects,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: query.refetch,
    findById,
    findByCode,
  };
}
